import React, { useRef, useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import queryString from 'querystringify';

import styles from './search.scss'; // @TODO tidy these up when removing non-disco search
import EnvVarsFeaturesContext from '../../scaffold/EnvVarsFeaturesContext';
import SearchInput from '../search-input/search-input';

const loadSearchBoxFiles = _callbackFunction => {
  if (typeof window !== 'undefined') {
    const baseJLUrl =
      window.location.host === 'www.johnlewis.com'
        ? 'https://www.johnlewis.com'
        : 'https://www.integration.project4.com';

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.type = 'text/css';
    link.href = `${baseJLUrl}/search-box-ui/static/index.css`;
    const cssLoaded = new Promise(resolve => {
      link.addEventListener('load', resolve);
    });
    document.head.appendChild(link);

    const script = document.createElement('script');
    const jsLoaded = new Promise(resolve => {
      script.addEventListener('load', resolve);
    });
    script.type = 'text/javascript';
    script.src = `${baseJLUrl}/search-box-ui/static/index.js`;
    document.head.appendChild(script);

    Promise.all([cssLoaded, jsLoaded])
      .then(() => {
        _callbackFunction();
      })
      .catch(err => {
        console.error('Error loading basic search files ', err);
      });
  } else {
    _callbackFunction();
  }
};

export const submitFormValidation = (id, box) => e => {
  const input = e.target.elements[id];
  if (input.value === '') {
    e.preventDefault();
    window.scrollTo(0, 0);
    box.current.focus();
  }
};

const Search = ({
  id,
  hideSearch,
  onFocus,
  isTypeaheadOpen,
  isBrandPromiseActive = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { features } = useContext(EnvVarsFeaturesContext);
  const URLQuery =
    typeof window === 'object' && queryString.parse(window.location.search);

  useEffect(() => {
    setSearchTerm(URLQuery['search-term'] || URLQuery.originalSearchTerm || '');
  }, []);

  const results = useRef(null);
  const container = useRef(null);
  const form = useRef(null);
  const box = useRef(null);
  const [loaded, setLoaded] = useState(null);

  const submitForm = submitFormValidation(id, box);

  const resetForm = () => {
    setSearchTerm('');
    window.scrollTo(0, 0);
    box.current.focus();
  };

  const typeaheadResultsClasses = isBrandPromiseActive
    ? classNames(styles.typeaheadResults, {
        [styles.typeaheadResults__hidden]: !isTypeaheadOpen,
      })
    : classNames(styles.typeaheadResults__bp, {
        [styles.typeaheadResults__bp__hidden]: !isTypeaheadOpen,
      });

  return (
    <div
      className={classNames(styles.form, {
        [styles.hide]: hideSearch,
      })}
      data-search-form
      ref={container}
      id="search-container"
    >
      <form
        data-testid="search-form"
        name="search-form"
        action="/search"
        className={styles.form}
        ref={form}
        onSubmit={submitForm}
        onReset={resetForm}
      >
        <label htmlFor={id} className={styles.visuallyHidden}>
          Search
        </label>
        <SearchInput
          id={id}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          loaded={loaded}
          loadSearchBoxFiles={loadSearchBoxFiles}
          setLoaded={setLoaded}
          results={results}
          container={container}
          form={form}
          box={box}
          features={features}
          ref={box}
          parentOnFocus={onFocus}
          isTypeaheadOpen={isTypeaheadOpen}
          showClear={Boolean(searchTerm.length)}
          isBrandPromiseActive={isBrandPromiseActive}
        />
      </form>
      <div ref={results} className={typeaheadResultsClasses} />
    </div>
  );
};

Search.defaultProps = {
  hideSearch: false,
  onFocus: () => {},
  isTypeaheadOpen: false,
};

Search.propTypes = {
  id: PropTypes.string.isRequired,
  hideSearch: PropTypes.bool,
  onFocus: PropTypes.func,
  isTypeaheadOpen: PropTypes.bool,
};

export default Search;
